/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { useFormik } from 'formik';
import { TOptions } from 'i18next';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';

import { ArrowRoundedIcon } from '../../../../../../components/svg-icons/ArrowRoundedIcon';
import { MIN_REPLICA_TIME_FOR_DOWNLOAD } from '../../../../../../constants/settings';
import { Namespaces } from '../../../../../../i18n';
import { TimeUnits } from '../../../../../../miscellaneous/timeUnits';
import { useFormStyles } from '../../../../../../styles/form';
import { useValidationTranslation } from '../../../../../../utils/useValidationTranslation';
import { validationSchema } from './DownloadNodeLogsForm.consts';
import { useDownloadNodeLogsFormStyles } from './DownloadNodeLogsForm.styles';
import { FormValues } from './DownloadNodeLogsForm.types';

type DownloadNodeLogsFormProps = {
  onSubmit: (values: FormValues) => void;
  onCancel: () => void;
};

const initialValues: FormValues = {
  time: MIN_REPLICA_TIME_FOR_DOWNLOAD,
  unit: TimeUnits.Min,
};

export const DownloadNodeLogsForm = memo<DownloadNodeLogsFormProps>(props => {
  const classes = useDownloadNodeLogsFormStyles();
  const formClasses = useFormStyles();
  const { onSubmit, onCancel } = props;
  const { t } = useTranslation(Namespaces.Cluster);
  const { vtr } = useValidationTranslation();

  const { handleSubmit, values, handleChange, touched, errors, handleBlur } = useFormik<FormValues>({
    enableReinitialize: true,
    validationSchema: validationSchema,
    initialValues,
    onSubmit: onSubmit,
  });

  const hasError = (field: keyof FormValues): boolean => {
    return !!touched[field] && !!errors[field];
  };

  const getError = (field: keyof FormValues, options?: TOptions): string => {
    const error = errors[field];
    if (error) {
      return vtr(error, options);
    }
    return '';
  };

  return (
    <form onSubmit={handleSubmit} className={classes.form}>
      <Typography variant="h4" className={classes.heading}>
        {t('details.replicas.download.title')}
      </Typography>
      <Grid container>
        <Grid item xs={9}>
          <FormControl
            className={cx(formClasses.formControl, formClasses.widthFull)}
            error={hasError('time')}
            variant="outlined"
          >
            <InputLabel htmlFor="time">{t('details.replicas.download.form.time')}</InputLabel>
            <OutlinedInput
              name="time"
              type="number"
              value={values.time}
              label={t('details.replicas.download.form.time')}
              autoFocus={true}
              fullWidth
              onChange={handleChange}
              onBlur={handleBlur}
            />
            {hasError('time') && (
              <FormHelperText>
                {getError('time', {
                  field: t('details.replicas.download.form.time'),
                  moreThan: MIN_REPLICA_TIME_FOR_DOWNLOAD,
                })}
              </FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl className={cx(formClasses.formControl, formClasses.widthFull, classes.pl10)} variant="outlined">
            <Select
              id="unit"
              value={values.unit}
              onChange={handleChange}
              IconComponent={ArrowRoundedIcon}
              inputProps={{
                name: 'unit',
              }}
            >
              {Object.values(TimeUnits).map((item, key: number) => (
                <MenuItem value={item} key={key}>
                  {item}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <div className={classes.popoverButons}>
        <Button variant="text" onClick={onCancel}>
          {t('details.replicas.download.form.buttons.cancel')}
        </Button>
        <Button variant="text" type="submit" data-cy="cluster-page-nodes-tab-download-logs-submit-button">
          {t('details.replicas.download.form.buttons.submit')}
        </Button>
      </div>
    </form>
  );
});
